import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Grid, LinearProgress } from '@mui/material';
import { AlertColor } from '@mui/material/Alert';
import useModuleMetadata from 'src/hooks/use-module-metadata.hook';
import { getDefaultOptionsForFormFields } from 'src/components/filters/utils/filter-form-utils';
import { IFilterObj } from 'src/components/filters';
import {
  getOutputColumnWithoutToggle,
  getTableColumnData,
  calculateValueUsingFormula
} from 'src/components/collapsible-table/table-column-prep';
import useMatomoFunction from 'src/hooks/use-matomo-function.hook';
// adding App Insights hook import for telemetry logging
import { useAppInsights } from 'src/hooks/useAppInsights';
import MODULE_TO_PAGE_MAP from '../home/types/simulation-module-to-page-map';
import SimulatorFooter from '../components/simulation-footer/simulator-footer';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import InnovationStrip from './components/innovation-strip/innovation-strip';
import {
  fetchMetadata,
  getInnovationTableData,
  getResetSimulatedInnovation,
  getSimulatedInnovationData
} from './store/innovation-slice-async-thunks';
import InnovationTable from './components/innovation-table/innovation-table';
import useModulePreRequisites from '../common/hooks/use-module-pre-requisites.hook';
import ScenarioCreateSuccessDialog from '../home/components/scenario-create-success/scenario-create-success-dialog';
import SnackbarComponent from './components/snackbar/snackbar';
import {
  FiltersWrapper,
  setGlobalFilterFieldOptions,
  setGlobalFilterValues
} from '../common/features';
import { setTableMetaData } from '../common/features/table/store/table-slice';
import {
  TableComponent,
  TypeOfTableData
} from '../pricing-simulator/types/generic-table.model';
import TableSkeletonContainer from '../pricing-simulator/components/simulation-table-container/table-skeleton';
import { MatomoEventTrackMapObject } from '../constants/constant';
import {
  toggleTableDataFetched,
  updateOverwrittenSimulateFlag,
  updateOvewrwrittenTableRow
} from './store/innovation-slice';
import { filterColumnsForToggleView } from '../pricing-simulator/utils/column-utils';
import OnNextClickHandler from '../common/utils/simulator-page-next-button';
import { getDataForInnovationBaseline } from '../../../../../src/components/collapsible-table/table-data-prep';
import { AlertDialog, DialogResult } from '../../../../../src/components/analytics/alert-dialog/index';



export default function Innovation() {
  // app dispatch for dispatching actions
  const appDispatch = useAppDispatch();
  const navigate = useNavigate();

  // adding App Insights hook initialization for telemetry logging
  const { trackEvent, trackException } = useAppInsights();

  // moving moduleId, sourceId, scenarioId to top to avoid "used before defined" errors
  const { moduleId, sourceId, scenarioId } = useModuleMetadata({
    module: MODULE_TO_PAGE_MAP.INNOVATION
  });

  // moving currentScenarioId to top to avoid "used before defined" errors
  const { id: currentScenarioId } = useParams();

  const { fetchingTableData, isMoSubmitDone, isMoSimulateDone } =
    useAppSelector((state) => state.innovation);

  const [columns, setColumns] = useState([]);

  const { tableMetadaData } = useAppSelector((state) => state.table);

  // local state for simulator footer click
  const [currClickedButton, setCurrClickedButton] = useState('');

  // local state for snackbar open/close
  const [isSnackbarOpen, setSnackbar] = useState(false);
  const [snackbarContent, setSnackbarContent] = useState<{
    snackbarText: string;
    snackbarType: AlertColor;
  }>({
    snackbarText: '',
    snackbarType: 'success'
  });

  const { dynamicBreadcrumbs } = useAppSelector(
    (state) => state.simulatorHome
  );
  const { innovation: innovationAccessConfig } = useAppSelector(
    (state) => state.simulatorHome.moduleAccessMap
  );

  const [isFetchingTableData, setIsFetchingTableData] = useState(true);

  const [externalReset, setExternalReset] = useState(false);

  const [isFilterValueChanged, setIsFilterValueChanged] = useState(false);

  const [isScenarioSuccessDialogOpen, setIsScenarioSuccessDialogOpen] =
    useState(false);

  const [scenarioSimulationInfo, setScenarioSimulationInfo] = useState({
    isDialogOpen: false,
    scenario: null,
    message: null
  });

  // localstate for manual overwrite dialog open/close
  const [isManualOverwriteDialogOpen, setIsManualOverwriteDialogOpen] =
    useState(false);

  // handler for manual overwrite dialog open
  const handleDialogOpen = () => {
    // adding telemetry log for manual overwrite dialog open
    trackEvent('Innovation_ManualOverwriteDialogOpened', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || '')
    });
    setIsManualOverwriteDialogOpen(true);
  };

  // handler for manual overwrite dialog close
  const handleDialogClose = () => {
    // adding telemetry log for manual overwrite dialog close
    trackEvent('Innovation_ManualOverwriteDialogClosed', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || '')
    });
    setIsManualOverwriteDialogOpen(false);
  };

  const { isFetchingPreRequisites } = useModulePreRequisites({
    module: MODULE_TO_PAGE_MAP.INNOVATION,
    onPreRequisitesFetched() {
      appDispatch(
        fetchMetadata({
          sourceID: sourceId,
          moduleID: moduleId,
          scenarioID: Number(scenarioId),
          filters: []
        })
      ).then((res) => {
        if (res.meta.requestStatus === 'rejected') {
          setSnackbarContent({
            // eslint-disable-next-line dot-notation
            snackbarText: res['error'].message,
            snackbarType: 'error'
          });
          setSnackbar(true);
        }
      });
    }
  });

  const getColumnsData = (tableResData: any) => {
    const { inputCols, resultCols, response } = tableResData;
    appDispatch(setTableMetaData({ tableData: response }));
    setColumns([
      ...getTableColumnData(
        inputCols,
        TableComponent.INNOVATION,
        response.header,
        []
      ),
      ...getOutputColumnWithoutToggle(
        resultCols,
        response.footer.row,
        {},
        TableComponent.INNOVATION
      )
    ]);
  };

  const handleFilterApply = (filters: IFilterObj[]) => {
    // adding telemetry log for filter apply action
    trackEvent('Innovation_FiltersApplied', {
      filterCount: filters?.length || 0,
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      sourceId: String(sourceId || '')
    });

    appDispatch(
      getInnovationTableData({
        sourceId,
        moduleId,
        scenarioId: Number(scenarioId),
        filters
      })
    ).then((res) => {
      if (res.meta.requestStatus === 'rejected') {
        setSnackbarContent({
          // eslint-disable-next-line dot-notation
          snackbarText: res['error'].message,
          snackbarType: 'error'
        });
        setSnackbar(true);
      } else {
        getColumnsData(res.payload);
      }
    });
  };

  // getting state of innnovation
  const {
    isSkuCRUDLoading,
    appliedFilters,
    isTableValidState,
    innovationTableMetaData,
    postSkuDeletionInnovationGridData,
    fetchingSimulatedData,
    isSimulated,
    isRowEdited,
    lastSimulatedSnapshot,
  } = useAppSelector((state) => state.innovation);

  const {
    globalFilterFormFields,
    globalFilterCombinations,
    globalFilterInitialValues
  } = useAppSelector((state) => state.filters);
  const { callFunction } = useMatomoFunction();

  const [isConfirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  const dialogMessage = "You have unsimulated changes on this page. \nMoving to the next module will not include these changes in the summary. Are you sure you want to continue?"
  const dialogTitle = ""

  const getPageName = () => {
    const page_title = window.location.href.split('/').at(-1);
    return page_title;
  };

  useEffect(() => {
    callFunction(
      MatomoEventTrackMapObject.track_page_view.event_key,
      getPageName()
    );

    // adding telemetry log for page load with business context
    // using String() to ensure type compatibility for scenarioId
    trackEvent('Innovation_PageLoaded', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      sourceId: String(sourceId || ''),
      hasSavedScenario: !!currentScenarioId,
      accessLevel: innovationAccessConfig?.save ? 'full' : 'read-only'
    });
  }, []);

  useEffect(() => {
    appDispatch(setTableMetaData({ tableData: innovationTableMetaData }));
    if (innovationTableMetaData.header.length) {
      setIsFetchingTableData(false);
    }
  }, [innovationTableMetaData]);

  // formatting table data for updating the footer value
  const getFormattedTableData = (tableDataResponse: any) => {
    const resultCols = filterColumnsForToggleView(
      [TypeOfTableData.OUTPUT],
      tableDataResponse.header,
      ''
    );
    const inputCols = filterColumnsForToggleView(
      [
        TypeOfTableData.DISPLAY,
        TypeOfTableData.LEGEND_FLAG,
        TypeOfTableData.ACTION,
        TypeOfTableData.INPUT
      ],
      tableDataResponse.header,
      ''
    );
    return {
      response: tableDataResponse,
      resultCols,
      inputCols
    };
  };

  // useEffect for updating the table data on change of footer value
  useEffect(() => {
    if (isRowEdited && innovationTableMetaData.footer.row.length) {
      const formattedData = getFormattedTableData(innovationTableMetaData);
      getColumnsData(formattedData);
      // making isRowEdited false after footer is updated
      appDispatch(toggleTableDataFetched());
    }
  }, [innovationTableMetaData.footer]);

  // updating the table component on change of innovation grid data on delete
  useEffect(() => {
    if (Object.keys(postSkuDeletionInnovationGridData).length > 0) {
      // updating table data when delete api gets fulfilled
      getColumnsData(postSkuDeletionInnovationGridData);
    }
  }, [postSkuDeletionInnovationGridData]);

  // handler for scenario dialog
  const handleScenarioCreateSuccessDialogClose = () => {
    setIsScenarioSuccessDialogOpen(false);
  };

  const [pendingNavigation, setPendingNavigation] = useState<{
    type: 'next' | 'breadcrumb';
    target?: string;
  } | null>(null);


  const checkUnsavedChanges = (compareAgainstSnapshot = false) => {
    const result = getChangedInputRows(compareAgainstSnapshot);
    return result?.isChanged ?? false;
  };

  const handleConfirmationDialogClose = (result: DialogResult) => {
    if (result === DialogResult.YES && pendingNavigation) {
      if (pendingNavigation.type === 'next') {
        OnNextClickHandler(
          dynamicBreadcrumbs,
          MODULE_TO_PAGE_MAP.INNOVATION,
          currentScenarioId,
          navigate
        );
      } else if (pendingNavigation.type === 'breadcrumb' && pendingNavigation.target) {
        navigate(pendingNavigation.target);
      }
    }
    setConfirmationDialogOpen(false);
    setPendingNavigation(null);
  };

  const getChangedInputRows = (compareAgainstSnapshot = false) => {
    // Only compare against snapshot if explicitly requested (Next/Breadcrumb)
    // AND a snapshot actually exists
    if (compareAgainstSnapshot && lastSimulatedSnapshot) {
      const simulatedData = getDataForInnovationBaseline(
        innovationTableMetaData,
        lastSimulatedSnapshot,
        'sku'
      );
      return {
        isChanged: simulatedData.inputs.length > 0 || simulatedData.data.length > 0,
        row: simulatedData
      };
    }

    // Fallback: use isRowEdited to detect unsaved changes
    return {
      isChanged: isRowEdited,
      row: null
    };
  };

  /**
   * Handler for simulate button click
   */
  const handleSimulationClick = () => {
    // adding telemetry log for simulation initiation with context
    trackEvent('Innovation_SimulationInitiated', {
      hasFilters: appliedFilters?.length > 0,
      isSimulated,
      scenarioId: String(scenarioId || 'new'),
      sourcePage: window.location.pathname,
      isRowEdited
    });

    // setting simulation click to simulate
    setCurrClickedButton('simulate');
    callFunction(
      MatomoEventTrackMapObject.track_simulate_button_click.event_key,
      getPageName()
    );
    appDispatch(
      getSimulatedInnovationData({
        sourceId,
        moduleId,
        scenarioId: Number(scenarioId),
        filters: appliedFilters
      })
    ).then((res: any) => {
      // checking if simulation result api failed
      if (res.meta.requestStatus === 'rejected') {
        setSnackbarContent({
          // eslint-disable-next-line dot-notation
          snackbarText: res['error'].message,
          snackbarType: 'error'
        });
        setSnackbar(true);
      } else {
        // adding telemetry log for successful simulation completion
        // using type assertion to safely access nested properties
        const payload = res.payload as { tableData?: { row?: any[] } };
        trackEvent('Innovation_SimulationCompleted', {
          rowCount: payload?.tableData?.row?.length || 0,
          scenarioId: String(scenarioId || 'new')
        });
        // updating table data when simulation api gets fulfilled
        getColumnsData(res.payload);
      }
    });
  };

  /**
   * handler for calling simulate api on IMO submit done
   */
  useEffect(() => {
    if (isMoSubmitDone === true) {
      handleSimulationClick();
      appDispatch(updateOverwrittenSimulateFlag());
    }
  }, [isMoSubmitDone]);

  const updateOutputSectionFields = (currentRow: any) => {
    const updatedCurrentRow: any = JSON.parse(JSON.stringify(currentRow));

    const filteredSection = innovationTableMetaData.header.filter(
      (item) => item.parent.section === 'output'
    );
    filteredSection.forEach((headerData) => {
      updatedCurrentRow.parent.data[headerData.parent.parent_field] =
        calculateValueUsingFormula(headerData, updatedCurrentRow);
    });

    return updatedCurrentRow;
  };

  useEffect(() => {
    if (!fetchingTableData && tableMetadaData.row?.length > 0) {
      // eslint-disable-next-line prefer-const
      let rowData = [...innovationTableMetaData.row];
      innovationTableMetaData.row.forEach((currRow, currIndex) => {
        const updatedRow = updateOutputSectionFields(currRow);
        rowData[currIndex] = updatedRow;
      });
      appDispatch(updateOvewrwrittenTableRow(rowData));
    }
  }, [fetchingTableData]);

  /**
   * Handler for simulation reset
   */
  const handleSimulationReset = () => {
    // adding telemetry log for simulation reset action
    trackEvent('Innovation_SimulationReset', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      wasSimulated: isSimulated,
      hasUnsavedChanges: isRowEdited
    });

    // setting simulation click to reset
    setCurrClickedButton('reset');
    setExternalReset(true);
    setTimeout(() => {
      setExternalReset(false);
    }, 2000);
    appDispatch(setGlobalFilterValues(globalFilterInitialValues));
    appDispatch(
      setGlobalFilterFieldOptions(
        getDefaultOptionsForFormFields(
          globalFilterFormFields,
          globalFilterCombinations,
          globalFilterInitialValues
        )
      )
    );
    appDispatch(
      getResetSimulatedInnovation({
        sourceId,
        moduleId,
        scenarioId: Number(scenarioId),
        filters: []
      })
    ).then((res: any) => {
      if (res.meta.requestStatus === 'rejected') {
        setSnackbarContent({
          // eslint-disable-next-line dot-notation
          snackbarText: res['error'].message,
          snackbarType: 'error'
        });
        setSnackbar(true);
      } else {
        getColumnsData(res.payload);
      }
    });
  };

  const handleBreadcrumbClick = (routeURL: string): boolean => {
    // If same module, allow navigation without dialog
    if (routeURL === window.location.pathname) {
      return true;
    }
    if (!checkUnsavedChanges(true)) {
      return true;
    }
    setPendingNavigation({ type: 'breadcrumb', target: routeURL });
    setConfirmationDialogOpen(true);
    return false;
  };

  //the handler function for the next button functionality
  const handleNextFn = () => {
    if (!checkUnsavedChanges(true)) {
      OnNextClickHandler(
        dynamicBreadcrumbs,
        MODULE_TO_PAGE_MAP.INNOVATION,
        currentScenarioId,
        navigate
      );
      return;
    }
    setPendingNavigation({ type: 'next' });
    setConfirmationDialogOpen(true);
  }

  /**
   * observer for simulate api for displaying dialog
   */
  useEffect(() => {
    setTimeout(() => {
      if (fetchingSimulatedData && currClickedButton !== 'reset') {
        setScenarioSimulationInfo({
          isDialogOpen: true,
          scenario: null,
          message: 'Simulating'
        });
      } else {
        setScenarioSimulationInfo({
          ...scenarioSimulationInfo,
          isDialogOpen: false
        });
      }
    }, 500);
  }, [fetchingSimulatedData]);

  if (isFetchingPreRequisites) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <LinearProgress />
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <Grid sx={{ padding: (theme) => theme.spacing(1, 2) }}>
        <FiltersWrapper
          accessConfig={innovationAccessConfig}
          externalReset={externalReset}
          moduleId={moduleId}
          onValueApply={handleFilterApply}
          onFilterFieldsFetched={handleFilterApply}
          scenarioId={scenarioId}
          setIsFilterValueChanged={(value) => {
            setIsFilterValueChanged(value);
          }}
          sourceId={sourceId}
        />
        <InnovationStrip
          currSourceId={sourceId}
          currModuleId={moduleId}
          currScenarioID={Number(scenarioId)}
        />
        <Box style={{ marginTop: '10px' }}>
          {isSkuCRUDLoading && <LinearProgress />}
          {tableMetadaData ? (
            <Grid container>
              {!fetchingTableData && tableMetadaData.row?.length > 0 ? (
                <Grid item xs={12}>
                  <InnovationTable columns={columns} />
                </Grid>
              ) : !fetchingTableData ? (
                <Grid item xs={12}>
                  <div
                    style={{
                      textAlign: 'center',
                      marginTop: '60px',
                      fontSize: '20px'
                    }}
                  >
                    No SKU'S Present.
                  </div>
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <TableSkeletonContainer rowLength={10} headerLength={6} />
                </Grid>
              )}
            </Grid>
          ) : (
            ''
          )}
        </Box>
      </Grid>

      <SnackbarComponent
        isSnackbarOpen={isSnackbarOpen}
        setSnackbar={setSnackbar}
        snackbarText={snackbarContent.snackbarText}
        snackbarType={snackbarContent.snackbarType}
      />

      {isScenarioSuccessDialogOpen ? (
        <ScenarioCreateSuccessDialog
          imagePath='/static/images/success.svg'
          isOpen={isScenarioSuccessDialogOpen}
          onClose={handleScenarioCreateSuccessDialogClose}
          message='Scenario has been successfully Saved'
        />
      ) : null}

      <ScenarioCreateSuccessDialog
        isOpen={scenarioSimulationInfo.isDialogOpen}
        imagePath='/static/images/file-text.svg'
        message={scenarioSimulationInfo.message}
      />
      <SimulatorFooter
        simulationClickHandler={handleSimulationClick}
        resetSimulationHandler={handleSimulationReset}
        isSimulationDisabled={
          !isTableValidState ||
          !innovationAccessConfig.simulate ||
          isFilterValueChanged
        }
        isResetSimulationDisabled={
          !innovationAccessConfig.reset_simulation ||
          !isSimulated ||
          !isTableValidState
        }
        nextClickHandler={handleNextFn}
        isButtonSectionVisible
        onBreadcrumbClick={handleBreadcrumbClick}
      />
      <AlertDialog
        closeHandler={handleConfirmationDialogClose}
        isDialogOpen={isConfirmationDialogOpen}
        message={dialogMessage}
        title={dialogTitle}
      />
    </>
  );
}